import React, { PropsWithChildren, ReactElement, Reducer, useReducer } from 'react';

import AuthAction, { ActionType } from '@/contexts/auth/AuthAction';
import AuthState from '@/contexts/auth/AuthState';
import { fetchUserInfo } from '@/contexts/auth/fetchUserInto';

interface AuthContextProviderProps {
  AuthContext: React.Context<AuthProviderProps>;
  AuthProvider: ({ children }: PropsWithChildren) => ReactElement;
}

interface ContextActions {
  login: () => void;
  errorLogin: () => void;
  cookieLogin: () => void;
  logout: () => void;
}

export type AuthProviderProps = AuthState & ContextActions;

const initialState: AuthState = {
  error: null,
  userInfo: null,
  fetchedUserInfo: false,
};

const authReducer: Reducer<AuthState, AuthAction> = (state: AuthState, action: AuthAction) => {
  switch (action.type) {
    case ActionType.login:
      return { userInfo: action.payload?.userInfo, error: null };
    case ActionType.cookieLogin:
      return { userInfo: action.payload?.userInfo, error: null, fetchedUserInfo: true };
    case ActionType.logout:
      return { error: null, userInfo: null };
    case ActionType.error:
      return { ...state, error: action.payload?.error, fetchedUserInfo: false };
    default:
      return state;
  }
};

const contextCreate = (): AuthContextProviderProps => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actions: any = { login, logout, cookieLogin, errorLogin };

  const AuthContext = React.createContext<AuthProviderProps>({ ...initialState, ...actions });

  const AuthProvider = ({ children }: PropsWithChildren): ReactElement => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    return (
      <AuthContext.Provider
        value={{
          ...state,
          login: () => login(dispatch),
          errorLogin: () => errorLogin(dispatch),
          cookieLogin: () => cookieLogin(dispatch, state),
          logout: () => logout(dispatch),
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  };

  return { AuthContext, AuthProvider };
};

const errorLogin = (dispatch: (action: AuthAction) => void) => {
  dispatch({ type: ActionType.error, payload: { error: 'Session login failed' } });
};

const cookieLogin = async (dispatch: (action: AuthAction) => void, state: AuthState) => {
  if (!state.userInfo) {
    const userinfo = await fetchUserInfo();
    window.athenaUserinfo = userinfo;

    dispatch({ type: ActionType.cookieLogin, payload: { userInfo: userinfo } });
  }
};

const login = (dispatch: (action: AuthAction) => void) => {
  dispatch({ type: ActionType.login });
  window.location.href = '/auth/signin';
};

const logout = (dispatch: (action: AuthAction) => void) => {
  dispatch({ type: ActionType.logout, payload: undefined });
  // MONOLITH: sessionStorage logic used only by monolith, remove after migration
  sessionStorage.removeItem('state');
  sessionStorage.setItem('shouldUpdateCartPreview', 'true');

  window.location.href = '/elysium-account/logout';
};

export const { AuthContext, AuthProvider } = contextCreate();
